// MOCK: meniru kontrak Cloud Function Lembar. Data hanya di localStorage peramban.
import { buildReading } from "@/lib/sheetReading";
import { createDecision, decisionIds } from "@/lib/decisionStore";
import { getConsent } from "@/lib/mockAuth";
import { getFlags } from "@/lib/flags";

import { accountStorage } from "./accountStorage";

const KEY = "axoris.mock.sheets";
const USAGE = "axoris.mock.sheetUsage";
const MAX_READINGS = 2;
const MAX_USER_BLOCKS = 3;
const WINDOW_CAP = 20;
const WINDOW_MS = 30 * 86400000;

export const KINDS = ["bebas", "tujuan", "sasaran", "objektif", "jurnal"];
export const STATUSES = ["open", "parked", "promoted", "archived"];

const sheetData = () => {
  const cache = accountStorage();
  return { cache, read: () => cache.read(KEY, []), write: (list) => cache.write(KEY, list) };
};
const delay = (ms = 300) => new Promise((r) => setTimeout(r, ms));
const failMode = () => new URLSearchParams(window.location.search).get("fail");
const now = () => new Date().toISOString();

export const jakartaDay = (iso) => new Date(iso).toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" });
export const todayJakarta = () => jakartaDay(new Date());

export const userBlocks = (s) => s.blocks.filter((b) => b.role === "user");
export const readings = (s) => s.blocks.filter((b) => b.role === "reading");
export const readingsLeft = (s) => MAX_READINGS - (s?.aiReadingsUsed ?? 0);
export const snippet = (s) => userBlocks(s)[0]?.text ?? "";

export const cutWord = (text, n) => {
  const v = text.trim();
  return v.length <= n ? v : v.slice(0, n).replace(/\s+\S*$/, "");
};
export const sheetTitle = (s) => (s.title?.trim() ? s.title.trim() : cutWord(snippet(s), 48));

export const validDate = (d) => /^\d{4}-\d{2}-\d{2}$/.test(d ?? "") && !Number.isNaN(Date.parse(d));
const invalid = (field) => ({ error: "sheet/invalid", field });
const len = (text) => (text ?? "").trim().length;

const reconcile = (list, cache) => {
  const ids = decisionIds();
  let touched = false;
  list.forEach((s) => {
    if (s.status === "promoted" && (!s.linkedDecisionId || !ids.includes(s.linkedDecisionId))) {
      s.status = "open";
      s.linkedDecisionId = null;
      touched = true;
    }
  });
  if (touched) cache.write(KEY, list);
  return list;
};

const usage = (cache) => cache.read(USAGE, null);
const windowOut = (cache) => {
  const u = usage(cache);
  return !!u && Date.now() - u.windowStart < WINDOW_MS && u.count >= WINDOW_CAP;
};
const bumpWindow = (cache) => {
  const u = usage(cache);
  const fresh = !u || Date.now() - u.windowStart >= WINDOW_MS;
  cache.write(USAGE, fresh ? { windowStart: Date.now(), count: 1 } : { ...u, count: u.count + 1 });
};

const find = (list, id) => list.find((s) => s.id === id);

export async function listSheets() {
  const { cache, read } = sheetData();
  await delay(350);
  if (!cache.isCurrent()) return { error: "sheet/missing" };
  if (failMode() === "sheets") return { error: "sheet/load" };
  return { sheets: reconcile(read(), cache).sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)) };
}

export async function getSheet(id) {
  const { cache, read } = sheetData();
  await delay(150);
  if (!cache.isCurrent()) return { error: "sheet/missing" };
  const s = find(reconcile(read(), cache), id);
  return s ? { sheet: s } : { error: "sheet/missing" };
}

export async function createSheet({ title, kind, entryDate, text }) {
  const { cache, read, write } = sheetData();
  if ((title ?? "").length > 80) return invalid("title");
  if (!KINDS.includes(kind)) return invalid("kind");
  if (entryDate && !validDate(entryDate)) return invalid("entryDate");
  if (len(text) < 20 || len(text) > 6000) return invalid("text");
  await delay();
  if (!cache.isCurrent()) return { error: "sheet/missing" };
  if (failMode() === "write") return { error: "sheet/write" };
  const t = now();
  const sheet = {
    id: `s_${Math.random().toString(36).slice(2, 10)}`,
    title: (title ?? "").trim(),
    kind,
    status: "open",
    blocks: [{ id: "b1", role: "user", text: text.trim(), at: t }],
    linkedDecisionId: null,
    aiReadingsUsed: 0,
    entryDate: entryDate || todayJakarta(),
    version: 1,
    createdAt: t,
    updatedAt: t,
  };
  write([...read(), sheet]);
  return { id: sheet.id, sheet };
}

export async function updateSheet({ id, version, title, kind, entryDate, blockId, text }) {
  const { cache, read, write } = sheetData();
  if ((title ?? "").length > 80) return invalid("title");
  if (!KINDS.includes(kind)) return invalid("kind");
  if (!validDate(entryDate)) return invalid("entryDate");
  const list = read();
  const s = find(list, id);
  if (!s) return { error: "sheet/missing" };
  if (s.version !== version) return { error: "sheet/version" };
  if (blockId) {
    const block = s.blocks.find((b) => b.id === blockId && b.role === "user");
    if (!block) return { error: "sheet/missing" };
    if (readings(s).some((r) => r.afterBlockId === blockId)) return { error: "sheet/closed" };
    const max = s.blocks[0].id === blockId ? 6000 : 2000;
    if (len(text) < 20 || len(text) > max) return invalid("text");
  }
  await delay();
  if (!cache.isCurrent()) return { error: "sheet/missing" };
  if (failMode() === "write") return { error: "sheet/write" };
  s.title = (title ?? "").trim();
  s.kind = kind;
  s.entryDate = entryDate;
  if (blockId) s.blocks.find((b) => b.id === blockId).text = text.trim();
  s.version += 1;
  s.updatedAt = now();
  write(list);
  return { version: s.version, sheet: s };
}

export async function addSheetBlock({ id, version, text }) {
  const { cache, read, write } = sheetData();
  if (len(text) < 20 || len(text) > 2000) return invalid("addText");
  const list = read();
  const s = find(list, id);
  if (!s) return { error: "sheet/missing" };
  if (s.version !== version) return { error: "sheet/version" };
  if (userBlocks(s).length >= MAX_USER_BLOCKS) return { error: "sheet/limit" };
  await delay();
  if (!cache.isCurrent()) return { error: "sheet/missing" };
  if (failMode() === "write") return { error: "sheet/write" };
  const blockId = `b${s.blocks.length + 1}`;
  s.blocks.push({ id: blockId, role: "user", text: text.trim(), at: now() });
  s.version += 1;
  s.updatedAt = now();
  write(list);
  return { blockId, version: s.version, sheet: s };
}

export async function readSheet({ id, version, lang }) {
  const { cache, read, write } = sheetData();
  const list = read();
  const s = find(list, id);
  if (!s) return { error: "sheet/missing" };
  if (s.version !== version) return { error: "sheet/version" };
  if (!getConsent()?.ai) return { error: "sheet/consent" };
  if (getFlags().aiPaused) return { error: "sheet/disabled" };
  if (readingsLeft(s) <= 0 || windowOut(cache)) return { error: "sheet/quota" };
  const last = s.blocks[s.blocks.length - 1];
  if (!last || last.role !== "user") return { error: "sheet/noread" };

  await delay(1200);
  if (!cache.isCurrent()) return { error: "sheet/missing" };
  if (failMode() === "read") return { error: "sheet/read" };

  const out = buildReading(userBlocks(s).map((b) => b.text), lang, s.title);
  const block = {
    id: `r${readings(s).length + 1}`,
    role: "reading",
    afterBlockId: last.id,
    summary: out.summary,
    signals: out.signals,
    questions: out.questions,
    abstained: out.abstained,
    model: null,
    promptVersion: "sheet_reading_v1",
    at: now(),
  };
  s.blocks.push(block);
  s.aiReadingsUsed += 1;
  s.version += 1;
  s.updatedAt = now();
  write(list);
  bumpWindow(cache);
  return { block, sheet: s };
}

export async function promoteSheet({ id, questionId, question, title }) {
  const { cache, read, write } = sheetData();
  if (len(question) < 12 || len(question) > 400) return invalid("question");
  if (len(title) < 3 || len(title) > 80) return invalid("draftTitle");
  const list = read();
  const s = find(list, id);
  if (!s) return { error: "sheet/missing" };
  const last = readings(s)[readings(s).length - 1];
  if (questionId && !last?.questions?.some((q) => q.id === questionId)) return invalid("question");
  await delay();
  if (!cache.isCurrent()) return { error: "sheet/missing" };
  if (failMode() === "write") return { error: "sheet/write" };
  const res = await createDecision({ title: title.trim(), question: question.trim(), desiredOutcome: "", horizonDays: 90 });
  if (res.error || !cache.isCurrent()) return { error: "sheet/write" };
  s.linkedDecisionId = res.decision.id;
  s.status = "promoted";
  s.version += 1;
  s.updatedAt = now();
  write(list);
  return { decisionId: res.decision.id, sheet: s };
}

export async function setSheetStatus({ id, status }) {
  const { cache, read, write } = sheetData();
  if (!STATUSES.includes(status)) return invalid("status");
  await delay(200);
  if (!cache.isCurrent()) return { error: "sheet/missing" };
  if (failMode() === "write") return { error: "sheet/write" };
  const list = read();
  const s = find(list, id);
  if (!s) return { error: "sheet/missing" };
  s.status = status;
  s.version += 1;
  s.updatedAt = now();
  write(list);
  return { status: s.status, sheet: s };
}

export async function deleteSheet(id) {
  const { cache, read, write } = sheetData();
  await delay(200);
  if (!cache.isCurrent()) return { error: "sheet/missing" };
  if (failMode() === "write") return { error: "sheet/write" };
  write(read().filter((s) => s.id !== id));
  return { ok: true };
}
